import React from "react";
import Tooltip from "@edaz/edazkit/Tooltip";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import { DataTable } from "@frui.ts/dataviews/dist";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist/dataTypes";
import { registerView, View } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views";
import { observer, Observer } from "mobx-react-lite";
import { formatDate, formatDateTime, formatLicensePlate, translateGeneral } from "utils/localizations";
import Paginator from "views/common/paginator";
import { emptyValue } from "utils/emptyValue";
import VehicleRegisterViewModel from "viewModels/callCenter/search/vehicleRegister/vehicleRegisterViewModel";
import type VehicleRegisterVehicle from "entities/vehicleRegisterVehicleResponse";
import type Vehicle from "entities/vehicleResponse";
import VehicleRegisterActionMenu from "./vehicleRegisterActionMenu";

const VehicleRegisterMessageView: ViewComponent<VehicleRegisterViewModel> = observer(({ vm }) => {
  const headerClassName = "py-4 text-wrap align-middle";
  const wrapClassName = "text-wrap";
  const noWrapClassName = "text-nowrap";
  const columns: ColumnDefinition<VehicleRegisterVehicle>[] = [
    {
      title: vm.translateAttribute("licensePlate"),
      headerClassName: headerClassName,
      cellProps: _ => ({ style: { minWidth: 100 } }),
      valueFormatter: props => {
        const vehicle: Vehicle = {
          licensePlate: props.item.licensePlate,
          countryId: props.item.countryId,
        };
        if (props.item.licensePlate) {
          const newLicensePlate = props.item.licensePlate;
          const originalLicensePlate = vm.filter.licensePlate;

          const licensePlateChangedInformation = originalLicensePlate && newLicensePlate !== originalLicensePlate;
          if (licensePlateChangedInformation && newLicensePlate && props.item.countryId) {
            const licensePlateChangeTooltip = vm
              .translate("licensePlateVehicleRegisterMessages")
              .replace("%originalLicensePlate%", originalLicensePlate)
              .replace("%newLicensePlate%", newLicensePlate);

            return (
              <Tooltip id="license-plate-tooltip" className="license-plate-tooltip" tooltip={licensePlateChangeTooltip ?? ""}>
                <span className="font-weight-bold text-secondary link-underline text-nowrap">
                  <span>{formatLicensePlate(vehicle)}</span>
                </span>
              </Tooltip>
            );
          }
          return formatLicensePlate(vehicle);
        }
      },
    },
    {
      title: vm.translateAttribute("licensePlateValidSinceDate"),
      property: "licensePlateValidSinceDate",
      cellClassName: wrapClassName,
      headerClassName: headerClassName,
      valueFormatter: props =>
        props.item.licensePlateValidSinceDate ? formatDate(props.item.licensePlateValidSinceDate) : emptyValue,
    },
    {
      title: vm.translateAttribute("licensePlateValidUntilDate"),
      property: "licensePlateValidUntilDate",
      cellClassName: wrapClassName,
      headerClassName: headerClassName,
      valueFormatter: props =>
        props.item.licensePlateValidUntilDate ? formatDate(props.item.licensePlateValidUntilDate) : emptyValue,
    },
    {
      title: vm.translateAttribute("vin"),
      property: "vin",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => props.item.vin ?? emptyValue,
    },
    {
      title: vm.translateAttribute("vehicleStatus"),
      property: "vehicleStatus",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => (props.item.vehicleStatus ? vm.translateVehicleStatus(props.item.vehicleStatus) : emptyValue),
    },
    {
      title: vm.translateAttribute("vehicleStatusValidSinceDate"),
      property: "vehicleStatusValidSinceDate",
      cellClassName: wrapClassName,
      headerClassName: headerClassName,
      valueFormatter: props =>
        props.item.vehicleStatusValidSinceDate ? formatDate(props.item.vehicleStatusValidSinceDate) : emptyValue,
    },
    {
      title: vm.translateAttribute("vehicleStatusValidUntilDate"),
      property: "vehicleStatusValidUntilDate",
      cellClassName: wrapClassName,
      headerClassName: headerClassName,
      valueFormatter: props =>
        props.item.vehicleStatusValidUntilDate ? formatDate(props.item.vehicleStatusValidUntilDate) : emptyValue,
    },
    {
      title: vm.translateAttribute("category"),
      property: "category",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => props.item.category ?? emptyValue,
    },
    {
      title: vm.translateAttribute("maximumAllowedWeight"),
      property: "maximumAllowedWeight",
      headerClassName: headerClassName,
      valueFormatter: props => (props.item.maximumAllowedWeight ? vm.formatHeight(props.item.maximumAllowedWeight) : emptyValue),
    },
    {
      title: vm.translateAttribute("classifiedFuelType"),
      property: "classifiedFuelType",
      headerClassName: headerClassName,
      valueFormatter: props =>
        props.item.classifiedFuelType
          ? vm.translateVehicleRegisterVehicleClassifiedFuelType(props.item.classifiedFuelType)
          : emptyValue,
    },
    {
      title: vm.translateAttribute("updatedAt"),
      property: "updatedAt",
      cellClassName: wrapClassName,
      headerClassName: headerClassName,
      valueFormatter: props => (props.item.updatedAt ? formatDateTime(props.item.updatedAt) : emptyValue),
    },
    {
      cellClassName: "text-align-end",
      headerClassName: headerClassName,
      valueFormatter: props => <VehicleRegisterActionMenu vm={vm} vehicleRegister={props.item} />,
    },
  ];

  return (
    <>
      <LoaderOverlay loading={vm.loading}>
        <DataTable
          headerRowClassName="bg-white"
          columns={columns}
          items={vm.items}
          itemKey={vm.idProperty}
          rowProps={item => {
            if (item === vm.openItem) {
              return {
                className: "table-active",
              };
            }
            return {
              className: vm.rowColor(item.vehicleStatus),
            };
          }}
          className="table mt-n4"
        />
        {vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("loading")}</div>
        )}
        {!vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("empty")}</div>
        )}
        <Paginator
          className="justify-content-center mt-4 mb-2"
          pagingInfo={vm.currentPaging}
          filter={vm.pagingFilter}
          onPageChanged={() => vm.loadData()}
        />
      </LoaderOverlay>
      <Observer>{() => <View vm={vm.activeChild} />}</Observer>
    </>
  );
});

registerView(VehicleRegisterMessageView, VehicleRegisterViewModel);
